<template>
  <KpiCard
    :label="label"
    icon="bi-person-slash"
    :accent="accent"
    :noZones="allUnmeasurable"
    :noZonesText="unmeasurableText"
    :empty="!withLimit.length && !allUnmeasurable"
    :emptyText="emptyText"
  >
    <template #value>{{ totalCount }}</template>
    <template #meta>{{ metaText }}</template>
  </KpiCard>
</template>

<script setup>
import { computed } from 'vue'
import KpiCard from '@/components/ui/KpiCard.vue'
import { useI18n } from 'vue-i18n'
import { currentLanguage } from '@/i18n'
import { formatDuration } from '@/i18n/format'
import { zoneTypeLabel } from '@/i18n/zoneTypes'

const { t } = useI18n()

const props = defineProps({
  // Every counter of ONE service. Never mixed across services — an info desk and a
  // checkout are different problems with different fixes, and one combined figure would
  // hide which. docs/reference/service_response_measurement.md §28.10
  counters: { type: Array, default: () => [] },
  accent: { type: String, default: '#0D9488' },
})

const measured = computed(() => props.counters.filter(c => c.state === 'measured'))
const needsSetup = computed(() => props.counters.filter(c => c.state === 'needs_setup'))
const allUnmeasurable = computed(() =>
  props.counters.length > 0 && props.counters.every(c => c.state === 'not_measurable'))

const label = computed(() =>
  props.counters[0]?.service === 'checkout' ? t('service.counter.labelCheckout') : t('service.counter.labelCounter'))
const isCheckout = computed(() => props.counters[0]?.service === 'checkout')

// Only counters whose owner set a "too long" limit (the Checkout Interaction zone's own
// TriggerActionThreshold, reused — see project_counter_unattended_measurement) carry a
// figure: a COUNT of customers who reached the counter and found NOBODY BEHIND IT for
// longer than that limit. A counter with no limit set shows no count — a figure built
// down at the raw cutoff is mostly camera flicker and would contradict the coach finding,
// which fires only past the owner's limit. Interaction zone, never "wait" (that is the
// queueing zone next door).
const withLimit = computed(() =>
  measured.value.filter(c => c.ownerThresholdWaits && c.ownerThresholdWaits.count != null))

// A count, deliberately, not a duration: customers who found this counter unattended past
// the limit. Each counter's own typical/longest live below, against the counter they
// belong to.
const totalCount = computed(() =>
  withLimit.value.reduce((n, c) => n + (c.ownerThresholdWaits.count || 0), 0))

const metaText = computed(() => {
  const m = withLimit.value
  if (!m.length) return ''
  const typicals = m.map(c => c.ownerThresholdWaits.typicalSec).filter(v => v != null).sort((a, b) => a - b)
  const longest = m.map(c => c.ownerThresholdWaits.longestSec).filter(v => v != null)
  const parts = []
  if (typicals.length === 1) parts.push(t('service.counter.metaTypically', { d: dur(typicals[0]) }))
  else if (typicals.length > 1) {
    const lo = dur(typicals[0]), hi = dur(typicals[typicals.length - 1])
    parts.push(t('service.counter.metaTypically', { d: lo === hi ? lo : `${lo}–${hi}` }))
  }
  if (longest.length) parts.push(t('service.counter.metaLongest', { d: dur(Math.max(...longest)) }))
  if (needsSetup.value.length) parts.push(t('service.counter.metaNotSetUp', { n: needsSetup.value.length }))
  return parts.length ? t('service.counter.metaPastLimit', { parts: parts.join(', ') }) : ''
})

// A zone type name as shown (the English name is data; Dutch shows its own).
const typeName = (name) => (currentLanguage() === 'en' ? name : zoneTypeLabel(name))

function dur(s) {
  if (s == null) return ''
  if (currentLanguage() !== 'en') return formatDuration(s)
  const n = Math.round(s)
  if (n < 60) return `${n}s`
  const m = Math.floor(n / 60)
  const r = n % 60
  return r ? `${m}m ${r}s` : `${m}m`
}

const unmeasurableText = computed(() => {
  const why = props.counters[0]?.why
  if (why === 'self_service') return t('service.counter.selfService')
  if (why === 'tripwire-only') return t('service.counter.tripwireMany')
  return t('service.counter.notMeasurable')
})

const emptyText = computed(() => {
  // Option A: a counter that IS measured but has no limit set shows the raw neutral facts
  // (how long nobody was behind it) and a nudge to set the limit — never a count, which
  // down at the raw cutoff is mostly flicker.
  if (measured.value.length) {
    const longest = measured.value.map(c => c.longestWaitSec).filter(v => v != null)
    const typicals = measured.value.map(c => c.typicalWaitSec).filter(v => v != null)
    const facts = []
    if (longest.length) facts.push(t('service.counter.metaLongest', { d: dur(Math.max(...longest)) }))
    if (typicals.length) facts.push(t('service.counter.metaTypically', { d: dur(Math.min(...typicals)) }))
    const lead = facts.length ? t('service.counter.emptyLead', { facts: facts.join(', ') }) : ''
    return `${lead}${isCheckout.value ? t('service.counter.setLimitCheckout') : t('service.counter.setLimitCounter')}`
  }
  // A counter not set up is NOT reported as zero — a zero reads as "nobody ever found it
  // empty", which we do not know. Name the zone type to draw.
  if (needsSetup.value.length) {
    const names = [...new Set(
      needsSetup.value.flatMap(c => (c.counterpartTypes || []).map(ct => typeName(ct.name))))]
    return names.length
      ? t('service.counter.addToMeasure', { names: names.join(` ${t('service.counter.or')} `) })
      : t('service.counter.addStaff')
  }
  return t('service.counter.measuring')
})
</script>
